import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import Sidebar from "../components/layout/Sidebar";
import DataTable, { TableColumn } from "../components/ui/DataTable";
import AddNewButton from "../components/ui/AddNewButton";
import Toggle from "../components/ui/Toggle";
import Modal from "../components/layout/Modal";
import DeleteModal from "../components/layout/DeleteModal";
import ConfirmModal from "../components/layout/ConfirmModal";
import EmptyState from "../components/ui/EmptyState";
import TablePagination from "../components/ui/TablePagination";
import CampaignForm, { CampaignFormValues } from "../components/campaigns/CampaignForm";
import { useCampaigns, useUpdateCampaign, useDeleteCampaign, useTriggerCampaignBroadcast, useCampaignBroadcast, useCampaignBroadcasts, Campaign } from "../services/campaigns";
import ROUTES from "../utils/ROUTES";
import { toast } from "../hooks/use-toast";
import StatusPill from "../components/ui/StatusPill";
import { Info, Mail, Pencil, Trash2 } from "lucide-react";
import axios from "axios";

const getErrorMessage = (err: unknown): string =>
  axios.isAxiosError(err) ? err.response?.data?.error ?? err.message : "Something went wrong. Please try again.";

const CampaignNameCell = ({ campaign }: { campaign: Campaign }) => {
  const { data: broadcasts } = useCampaignBroadcasts(campaign.id);
  const noEmailsSent = Array.isArray(broadcasts) && broadcasts.length === 0;
  return (
    <div className="flex flex-col gap-[2px]">
      <div className="flex items-center gap-1">
        <span className="text-[16px] font-bold leading-none text-content-heading">{campaign.name}</span>
        {noEmailsSent && (
          <div className="relative flex items-center group/tooltip">
            <Info className="w-[14px] h-[14px] text-content-muted cursor-pointer shrink-0" />
            <div className="absolute left-5 top-1/2 -translate-y-1/2 z-20 hidden group-hover/tooltip:block w-max max-w-[220px] rounded-[6px] bg-content-heading px-3 py-2 text-[12px] text-white shadow-lg">
              Broadcast emails have not been sent to customers and must be sent manually.
            </div>
          </div>
        )}
      </div>
      <span className="text-[14px] font-normal text-content-label">{campaign.description.length > 250 ? campaign.description.slice(0, 250) + "…" : campaign.description}</span>
    </div>
  );
};

const Campaigns = () => {
  const navigate = useNavigate();
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [pollingCampaignId, setPollingCampaignId] = useState<string | null>(() =>
    localStorage.getItem("campaign_broadcast_polling_id")
  );
  const [pollingBroadcastId, setPollingBroadcastId] = useState<number | null>(() => {
    const stored = localStorage.getItem("campaign_broadcast_polling_broadcast_id");
    return stored ? Number(stored) : null;
  });
  const [editingCampaign, setEditingCampaign] = useState<Campaign | null>(null);
  const [activateOnSave, setActivateOnSave] = useState(false);
  const [deletingCampaign, setDeletingCampaign] = useState<Campaign | null>(null);
  const [broadcastingCampaign, setBroadcastingCampaign] = useState<Campaign | null>(null);

  const { data: campaignsData, isLoading, isError } = useCampaigns(currentPage);
  const campaigns = campaignsData?.campaigns;
  const pagination = campaignsData?.pagination;
  const { mutate: updateCampaign } = useUpdateCampaign();
  const { mutate: deleteCampaign } = useDeleteCampaign();
  const { mutate: triggerBroadcast } = useTriggerCampaignBroadcast();
  const { data: broadcastData } = useCampaignBroadcast(
    pollingCampaignId ?? "",
    pollingBroadcastId ?? 0,
    { polling: !!pollingCampaignId && !!pollingBroadcastId }
  );
  const isBroadcastActive =
    broadcastData?.status === "pending" || broadcastData?.status === "processing";

  const updatePollingIds = (campaignId: string | null, broadcastId: number | null) => {
    setPollingCampaignId(campaignId);
    setPollingBroadcastId(broadcastId);
    if (campaignId === null) {
      localStorage.removeItem("campaign_broadcast_polling_id");
      localStorage.removeItem("campaign_broadcast_polling_broadcast_id");
    } else {
      localStorage.setItem("campaign_broadcast_polling_id", campaignId);
      localStorage.setItem("campaign_broadcast_polling_broadcast_id", String(broadcastId));
    }
  };

  useEffect(() => {
    if (pollingCampaignId && broadcastData && !isBroadcastActive) {
      updatePollingIds(null, null);
    }
  }, [broadcastData, isBroadcastActive, pollingCampaignId]);

  const handleConfirmDelete = () => {
    if (!deletingCampaign) return;
    deleteCampaign(deletingCampaign.id, {
      onSuccess: () => setDeletingCampaign(null),
    });
  };

  const handleConfirmBroadcast = () => {
    if (!broadcastingCampaign) return;
    triggerBroadcast(broadcastingCampaign.id, {
      onSuccess: (b) => { updatePollingIds(broadcastingCampaign.id, b.broadcast_id); setBroadcastingCampaign(null); },
      onError: (err) => { toast({ variant: "error", title: "Failed to send broadcast", description: getErrorMessage(err) }); setBroadcastingCampaign(null); },
    });
  };

  const handleSave = (values: CampaignFormValues) => {
    if (!editingCampaign) return;
    updateCampaign(
      { id: editingCampaign.id, body: { ...values, ...(activateOnSave && { is_enabled: true }) } },
      {
        onSuccess: () => { toast({ variant: "success", title: "Campaign updated", description: "The campaign has been successfully updated." }); setEditingCampaign(null); setActivateOnSave(false); },
        onError: (err) => { toast({ variant: "error", title: "Failed to update campaign", description: getErrorMessage(err) }); },
      }
    );
  };

  const handleToggle = (campaign: Campaign, is_enabled: boolean) => {
    if (is_enabled && new Date(campaign.expiry_date) < new Date()) {
      setEditingCampaign(campaign);
      setActivateOnSave(true);
      return;
    }
    const { name, description, start_date, expiry_date, image_url } = campaign;
    updateCampaign({ id: campaign.id, body: { name, description, start_date, expiry_date, image_url, is_enabled } });
  };

  const columns: TableColumn<Campaign>[] = [
    {
      key: "image_url",
      header: "Image",
      cellClassName: "px-6 py-4",
      render: (row) => (
        <div className="w-12 h-12 rounded-[2px] border border-[#c2c7c9] overflow-hidden shrink-0">
          <img src={row.image_url} alt={row.name} className="w-full h-full object-cover" />
        </div>
      ),
    },
    {
      key: "name",
      header: "Campaign Name",
      cellClassName: "px-6 py-[22px]",
      render: (row) => <CampaignNameCell campaign={row} />,
    },
    {
      key: "expiry_date",
      header: "Expiry Date",
      cellClassName: "px-6 py-[30px] whitespace-nowrap",
      render: (row) => (
        <span className="text-[14px] text-content-body">
          {new Date(row.expiry_date).toLocaleDateString("en-GB", { day: "2-digit", month: "short", year: "numeric" })}
        </span>
      ),
    },
    {
      key: "status",
      header: "Status",
      cellClassName: "px-6 py-[30px]",
      render: (row) => <StatusPill status={row.is_enabled ? "Active" : "InActive"} />,
    },
    {
      key: "actions",
      header: "Actions",
      headerAlign: "right",
      width: "180px",
      cellClassName: "px-6 py-4",
      render: (row) => (
        <div className="flex items-center justify-end gap-1">
          <div className="relative group">
            <button onClick={() => setEditingCampaign(row)} className="p-[6px] hover:opacity-70 transition-opacity" aria-label="Edit campaign">
              <Pencil className="w-[15px] h-[15px]" />
            </button>
            <span className="pointer-events-none absolute bottom-full left-1/2 -translate-x-1/2 mb-1 hidden group-hover:block whitespace-nowrap rounded-[4px] bg-content-heading px-2 py-1 text-[11px] text-white shadow-md z-20">Edit campaign</span>
          </div>
          <div className="relative group">
            <button onClick={() => setDeletingCampaign(row)} className="p-[6px] hover:opacity-70 transition-opacity" aria-label="Delete campaign">
              <Trash2 className="w-[13px] h-[15px]" />
            </button>
            <span className="pointer-events-none absolute bottom-full left-1/2 -translate-x-1/2 mb-1 hidden group-hover:block whitespace-nowrap rounded-[4px] bg-content-heading px-2 py-1 text-[11px] text-white shadow-md z-20">Delete campaign</span>
          </div>
          {row.is_enabled && (
            <div className="relative group">
              <button
                onClick={() => setBroadcastingCampaign(row)}
                disabled={row.id === pollingCampaignId && isBroadcastActive}
                className="p-[6px] hover:opacity-70 transition-opacity text-content-label disabled:opacity-30 disabled:cursor-not-allowed"
                aria-label="Send broadcast email"
              >
                <Mail className="w-[15px] h-[15px]" />
              </button>
              <span className="pointer-events-none absolute bottom-full left-1/2 -translate-x-1/2 mb-1 hidden group-hover:block whitespace-nowrap rounded-[4px] bg-content-heading px-2 py-1 text-[11px] text-white shadow-md z-20">Send broadcastemail</span>
            </div>
          )}
          <div className="pl-2 flex items-center">
            <Toggle enabled={row.is_enabled} onChange={(val) => handleToggle(row, val)} />
          </div>
        </div>
      ),
    },
  ];

  return (
    <div className="flex h-screen overflow-hidden bg-surface-page">
      <Sidebar isOpen={sidebarOpen} onClose={() => setSidebarOpen(false)} />

      <main className="flex-1 min-w-0 overflow-y-auto px-4 sm:px-8 lg:pl-11 lg:pr-8 pt-6 lg:pt-[60px] pb-16">
        <div className="flex items-center mb-4 lg:hidden">
          <button onClick={() => setSidebarOpen(true)} className="p-2 rounded-lg hover:bg-black/5 transition-colors" aria-label="Open menu">
            <svg className="w-6 h-6" fill="none" stroke="#012926" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
            </svg>
          </button>
        </div>

        <div className="flex items-start justify-between gap-4">
          <div>
            <h1 className="text-[28px] sm:text-[34px] uppercase tracking-[0.68px] leading-none text-brand-dark" style={{ fontFamily: '"Bebas Neue", sans-serif' }}>
              Campaigns
            </h1>
            <p className="mt-3 text-[14px] sm:text-[16px] font-medium text-content-label">
              Real-time overview of program health and customer engagement.
            </p>
          </div>
          <AddNewButton onClick={() => navigate(ROUTES.ADD_CAMPAIGN)} />
        </div>

        <div className="mt-8 lg:mt-[36px]">
          {isLoading && <p className="text-[14px] text-content-label">Loading…</p>}
          {isError && <p className="text-[14px] text-content-error">Failed to load campaigns.</p>}
          {!isLoading && !isError && (
            !campaigns?.length
              ? <EmptyState message="No campaigns found." />
              : (
                  <>
                    <DataTable columns={columns} rows={campaigns!} rowKey={(c) => c.id} />
                    <TablePagination currentPage={currentPage} pagination={pagination} rowCount={campaigns!.length} onPageChange={setCurrentPage} />
                  </>
                )
          )}
        </div>
      </main>

      <Modal isOpen={editingCampaign !== null} onClose={() => { setEditingCampaign(null); setActivateOnSave(false); }} title={`Edit "${editingCampaign?.name ?? ""}"`}>
        <CampaignForm
          initialValues={{
            name: editingCampaign?.name,
            description: editingCampaign?.description,
            start_date: editingCampaign?.start_date?.split("T")[0],
            expiry_date: editingCampaign?.expiry_date?.split("T")[0],
            image_url: editingCampaign?.image_url,
          }}
          onSave={handleSave}
        />
      </Modal>

      <DeleteModal
        isOpen={deletingCampaign !== null}
        onClose={() => setDeletingCampaign(null)}
        onConfirm={handleConfirmDelete}
        title="Are you sure you want to delete this campaign?"
        message={<>This action will permanently remove the <span className="font-semibold text-content-body" style={{ fontFamily: "Inter, sans-serif" }}>&ldquo;{deletingCampaign?.name}&rdquo;</span> campaign from your system. This action cannot be undone.</>}
        confirmLabel="Delete Campaign"
        cancelLabel="Cancel"
      />

      <ConfirmModal
        isOpen={broadcastingCampaign !== null}
        onClose={() => setBroadcastingCampaign(null)}
        onConfirm={handleConfirmBroadcast}
        title="Send broadcast email?"
        message={<>This will send a broadcast email for the <span className="font-semibold text-content-body" style={{ fontFamily: "Inter, sans-serif" }}>&ldquo;{broadcastingCampaign?.name}&rdquo;</span> campaign to all eligible customers.</>}
        confirmLabel="Send Broadcast"
        cancelLabel="Cancel"
      />
    </div>
  );
};

export default Campaigns;
